import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ReviewInputField {
  id: string;
  label?: string;
  text: string;
}

export interface ReviewResultField {
  id: string;
  label: string;
  original: string;
  polished: string;
  changed: boolean;
}

/**
 * Låter AI:n språkgranska alla fritextfält i ett dokument i en körning.
 */
export const useAiDocumentReview = () => {
  const [isReviewing, setIsReviewing] = useState(false);

  const review = useCallback(
    async (documentName: string, fields: ReviewInputField[]): Promise<ReviewResultField[] | null> => {
      const filled = fields.filter((f) => (f.text ?? "").trim().length > 0);
      if (filled.length === 0) {
        toast.info("Inget att granska", {
          description: "Fyll i minst ett fritextfält först.",
        });
        return null;
      }

      setIsReviewing(true);
      try {
        const { data, error } = await supabase.functions.invoke("ai-review-document", {
          body: { documentName, fields: filled },
        });

        if (error) {
          let details = error.message;
          try {
            const ctx = (error as any)?.context;
            if (ctx?.text) {
              const parsed = JSON.parse(await ctx.text());
              if (parsed?.error) details = parsed.error;
            }
          } catch {
            // behåll ursprungligt felmeddelande
          }
          toast.error("AI-granskningen kunde inte köras", { description: details });
          return null;
        }

        if (data?.error) {
          toast.error("AI-granskningen kunde inte köras", { description: data.error });
          return null;
        }

        return (data?.fields ?? []) as ReviewResultField[];
      } catch (e: any) {
        toast.error("AI-granskningen kunde inte köras", { description: e?.message });
        return null;
      } finally {
        setIsReviewing(false);
      }
    },
    [],
  );

  return { review, isReviewing };
};

export default useAiDocumentReview;
